import React, { useState } from "react";
import { Box, Typography, TextField, IconButton, Tooltip, Button } from "@mui/material";
import { Add, Delete, ArrowUpward, ArrowDownward } from "@mui/icons-material";
import styled from "@emotion/styled";

const Heading = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(0.5),
}));

const HelperText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
}));

const EmptyText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    marginBottom: theme.spacing(2),
}));

const ItemRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
}));

const AddRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
}));

const TASK_TEMPLATE_HELPER = "These steps will be added to the task as a checklist each time this template is used.";
const TASK_TEMPLATE_EMPTY = "No checklist items yet. Add steps that should be ticked off while working this task.";

const StepChecklist = ({ items, onChange, heading = "Checklist", helperText = TASK_TEMPLATE_HELPER, emptyText = TASK_TEMPLATE_EMPTY, itemPlaceholder = "Checklist step", addPlaceholder = "Add a step" }) => {
    const [newItemName, setNewItemName] = useState("");

    const list = Array.isArray(items) ? items : [];

    const handleAdd = () => {
        const name = newItemName.trim();
        if (!name) return;
        onChange([...list, { id: crypto.randomUUID(), name }]);
        setNewItemName("");
    };

    const handleRename = (index, name) => {
        onChange(list.map((item, i) => (i === index ? { ...item, name } : item)));
    };

    const handleRemove = (index) => {
        onChange(list.filter((_, i) => i !== index));
    };

    const handleMove = (index, offset) => {
        const target = index + offset;
        if (target < 0 || target >= list.length) return;
        const next = [...list];
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };

    return (
        <Box>
            <Heading>{heading}</Heading>
            <HelperText variant="body2">{helperText}</HelperText>

            {list.length === 0 ? (
                <EmptyText variant="body2">{emptyText}</EmptyText>
            ) : (
                list.map((item, index) => (
                    <ItemRow key={item.id ?? index}>
                        <TextField
                            value={item.name}
                            onChange={(e) => handleRename(index, e.target.value)}
                            variant="standard"
                            fullWidth
                            placeholder={itemPlaceholder}
                        />
                        <Tooltip title="Move up">
                            <span>
                                <IconButton size="small" onClick={() => handleMove(index, -1)} disabled={index === 0} aria-label="move up">
                                    <ArrowUpward fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                            <span>
                                <IconButton size="small" onClick={() => handleMove(index, 1)} disabled={index === list.length - 1} aria-label="move down">
                                    <ArrowDownward fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Remove item">
                            <IconButton size="small" onClick={() => handleRemove(index)} aria-label="remove item">
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </ItemRow>
                ))
            )}

            <AddRow>
                <TextField
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleAdd();
                        }
                    }}
                    placeholder={addPlaceholder}
                    variant="standard"
                    fullWidth
                />
                <Button size="small" startIcon={<Add />} onClick={handleAdd} disabled={!newItemName.trim()}>
                    Add item
                </Button>
            </AddRow>
        </Box>
    );
};

export default StepChecklist;
